<template>
  <div class="chat-table-content">
    <div class="table-with-content" v-if="totalRowCount > 0">
      <!-- Page Size Selector -->
      <div class="pagination-controls">
        <!-- Pagination Info -->
        <div class="pagination-info">
          Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalRowCount }} results
        </div>
      </div>

      <!-- Table -->
      <div class="table-container">
        <table class="v-datatable v-table mt-0">
          <thead>
            <tr>
              <th>#</th>
              <th v-for="header in headerValues" :key="`${header.name}-header`" :class="header.type">
                <div class="header-content">
                  <div class="header-title" :title="header.name">{{ header.displayName || title(header.name) }}</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="pageLoading" :key="'loading'">
              <td :colspan="headerValues.length + 1" class="loading-cell">Loading...</td>
            </tr>
            <tr v-else v-for="(row, index) in paginatedData" :key="`${message.id}-row-${startIndex + index}`"
              @click="linkTo(row)" :class="{ 'data-row': field(row, 'id').value !== undefined }">
              <td>
                <div v-if="isLinkableData">
                  <router-link :to="{ name: 'Case', params: { case_id: field(row, 'id').value } }">
                    {{ startIndex + index + 1 }}
                  </router-link>
                </div>
                <div v-else>{{ startIndex + index + 1 }}</div>
              </td>
              <td v-for="(fieldName, fieldIndex) in headerValues"
                :key="`${message.id}-row-${startIndex + index}-key-${fieldIndex}`"
                :class="field(row, fieldName.name) ? field(row, fieldName.name).data_type : ''">
                <router-link
                  v-if="isNestedObject(field(row, fieldName.name)) && field(row, fieldName.name).value && field(row, fieldName.name).value.id"
                  :to="{ name: 'Case', params: { case_id: field(row, fieldName.name).value.id } }"
                  class="nested-object-link" @click.stop>
                  {{ formattedValue(field(row, fieldName.name)) }}
                </router-link>
                <span v-else>
                  {{ formattedValue(field(row, fieldName.name)) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Navigation -->
      <div class="pagination-navigation">
        <button @click="previousPage" :disabled="currentPage === 1" class="pagination-btn"
          :class="{ 'disabled': currentPage === 1 }">
          Previous
        </button>

        <div class="page-numbers">
          <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" class="page-btn"
            :class="{ 'active': page === currentPage, 'disabled': page === '...' }">
            {{ page }}
          </button>
        </div>

        <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn"
          :class="{ 'disabled': currentPage === totalPages }">
          Next
        </button>
      </div>
    </div>
    <div v-else>
      <p>No data available for table</p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import helpers from '@/helpers'
import { formatField, shouldExcludeExportField } from '@/stores/chat/helpers.js'
import { getTablePage } from '@/services/chatTableStorage.js'

export default {
  name: "ChatMessageTable",
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
      pageLoading: false,
      currentPageRows: []
    }
  },
  computed: {
    ...mapState({
      caseTypes: state => state.caseTypes.all
    }),
    isIndexedDb() {
      return this.message.data && this.message.data.__indexedDb === true
    },
    totalRowCount() {
      return this.isIndexedDb ? this.message.data.totalRows : this.data.length
    },
    totalPages() {
      return Math.ceil(this.totalRowCount / this.pageSize)
    },
    startIndex() {
      return (this.currentPage - 1) * this.pageSize
    },
    endIndex() {
      return Math.min(this.startIndex + this.pageSize, this.totalRowCount)
    },
    paginatedData() {
      if (this.isIndexedDb) {
        return this.currentPageRows
      }
      return this.data.slice(this.startIndex, this.endIndex)
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        if (current > 3) {
          pages.push('...')
        }
        const start = Math.max(2, current - 1)
        const end = Math.min(total - 1, current + 1)
        for (let i = start; i <= end; i++) {
          pages.push(i)
        }
        if (current < total - 2) {
          pages.push('...')
        }
        if (total > 1) {
          pages.push(total)
        }
      }
      return pages
    },
    data() {
      if (this.isIndexedDb) return []
      return this.message.data.map((row) => {
        const newRow = []
        row.forEach((field) => {
          if (Array.isArray(field.value)) {
            for (let i = 0; i < field.value.length; i++) {
              const newObj = Object.assign({}, field, { field_name: field.field_name[i], value: field.value[i] })
              newRow.push(newObj)
            }
          } else {
            newRow.push(field)
          }
        })
        return newRow
      })
    },
    isLinkableData() {
      if (this.isIndexedDb) return this.message.data.hasIdField === true
      return this.data.length > 0 && this.data[0].find((f) => {
        const fn = Array.isArray(f.field_name) ? f.field_name[0] : f.field_name
        return fn === "id"
      }) !== undefined
    },
    headerValues() {
      const values = []
      const shouldExcludeField = (fieldName) => {
        // Keep id on the row for click-through, but never as a visible column.
        if (fieldName === "id") return false
        return shouldExcludeExportField(fieldName)
      }
      const pushRowFields = (row) => {
        if (!row || !row.length) return
        row.forEach((field) => {
          const names = Array.isArray(field.field_name) ? field.field_name : [field.field_name]
          names.forEach((f) => {
            if (!shouldExcludeField(f) && values.every((v) => v.name !== f) && f !== "id") {
              values.push({ name: f, type: field.data_type, displayName: field.display_name })
            }
          })
        })
      }
      // Large tables: stored headers were computed at persist time and miss new columns; derive from loaded rows.
      // Union across rows — unset fields (e.g. vor_date) may be absent from row 0.
      if (this.isIndexedDb) {
        if (this.currentPageRows && this.currentPageRows.length) {
          this.currentPageRows.forEach((row) => pushRowFields(row))
          if (values.length) return values
        }
        return (this.message.data && this.message.data.headers) || []
      }
      const rows = this.message.data
      if (Array.isArray(rows)) {
        rows.forEach((row) => pushRowFields(row))
      }
      return values
    }
  },
  methods: {
    formattedValue(field) {
      return formatField(field)
    },
    title(text) {
      return helpers.titleize(text.replaceAll(".", " "))
    },
    field(row, fieldName) {
      const found = row.find((r) => {
        const fn = Array.isArray(r.field_name) ? r.field_name[0] : r.field_name
        return fn === fieldName
      })
      return found || {}
    },
    isNestedObject(field) {
      if (!field || !field.value) {
        return false
      }
      return typeof field.value === "object" &&
        !Array.isArray(field.value) &&
        field.value !== null &&
        (field.value.id !== undefined || field.value.title !== undefined || field.value._title !== undefined)
    },
    linkTo(row) {
      // Optional: navigate to case on row click
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    goToPage(page) {
      if (page !== '...' && page >= 1 && page <= this.totalPages) {
        this.currentPage = page
      }
    },
    async loadPageFromIndexedDb() {
      if (!this.isIndexedDb || !(this.message.data && this.message.data.key)) return
      this.pageLoading = true
      try {
        const rows = await getTablePage(
          this.message.data.key,
          this.startIndex,
          this.pageSize
        )
        const transformed = rows.map((row) => {
          const newRow = []
          row.forEach((field) => {
            if (Array.isArray(field.value)) {
              for (let i = 0; i < field.value.length; i++) {
                newRow.push(Object.assign({}, field, {
                  field_name: field.field_name[i],
                  value: field.value[i]
                }))
              }
            } else {
              newRow.push(field)
            }
          })
          return newRow
        })
        this.currentPageRows = transformed
      } catch (e) {
        console.error("Failed to load table page from IndexedDB:", e)
        this.currentPageRows = []
      } finally {
        this.pageLoading = false
      }
    }
  },
  mounted() {
    if (this.isIndexedDb) this.loadPageFromIndexedDb()
  },
  watch: {
    pageSize() {
      this.currentPage = 1
    },
    currentPage() {
      if (this.isIndexedDb) this.loadPageFromIndexedDb()
    },
    "message.data.key"() {
      if (this.isIndexedDb) {
        this.currentPage = 1
        this.loadPageFromIndexedDb()
      }
    },
    "message.id"() {
      if (this.isIndexedDb) {
        this.currentPage = 1
        this.currentPageRows = []
        this.loadPageFromIndexedDb()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .chat-table-content {
    padding: 40px 10px;

    .pagination-controls {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 20px;
    padding: 0 20px;

    .page-size-selector {
      display: flex;
      align-items: center;
      gap: 10px;

      label {
        font-size: 14px;
        color: #666;
      }

      .page-size-select {
        padding: 5px 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        font-size: 14px;
      }
    }

    .pagination-info {
      font-size: 14px;
      color: #666;
    }
  }

  .pagination-navigation {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
    padding: 0 20px;

    .pagination-btn {
      padding: 8px 16px;
      border: 1px solid #ddd;
      background: white;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.2s;

      &:hover:not(.disabled) {
        background: #f5f5f5;
        border-color: #ccc;
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .page-numbers {
      display: flex;
      gap: 5px;

      .page-btn {
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        min-width: 40px;
        transition: all 0.2s;

        &:hover:not(.disabled) {
          background: #f5f5f5;
          border-color: #ccc;
        }

        &.active {
          background: rgb(29, 47, 62, 1);
          color: white;
          border-color: rgb(29, 47, 62, 1);
        }

        &.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }

  .table-container {
    width: 100%;
    border-radius: 10px;
    border-spacing: 0;
    overflow-x: scroll;
    border-collapse: separate;
    display: block;

    table.v-table {
      width: 100%;
      border: 1px solid #ccc;
      border-radius: 10px;
      border-spacing: 0;
      overflow: hidden;
      border-collapse: separate;

      thead th:first-child {
        border-top-left-radius: 10px;
      }

      thead th:last-child {
        border-top-right-radius: 10px;
      }

      tbody tr:last-child td:first-child {
        border-bottom-left-radius: 10px;
      }

      tbody tr:last-child td:last-child {
        border-bottom-right-radius: 10px;
      }

      th,
      td {
        padding: 9px;
        height: initial;
        border: 0.5px solid #ccc;
      }

      th {
        text-align: left;
        background-color: var(--table-header-background, #f5f5f5);
        color: var(--table-header-color, #333);
        padding: 12px;

        &.currency,
        &.number,
        &.date_part {
          text-align: right
        }

        .header-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .header-title {
          font-weight: 600;
          font-size: 14px;
          line-height: 1.2;
        }

        .header-field-name {
          font-size: 11px;
          color: #666;
          font-weight: normal;
          font-family: monospace;
          opacity: 0.7;
          line-height: 1.2;
        }
      }

      td {
        white-space: nowrap;

        &.currency,
        &.number,
        &.date_part {
          text-align: right
        }

        &.loading-cell {
          text-align: center;
          padding: 24px;
          color: #666;
        }
      }
    }
  }

  .nested-object-link {
    color: var(--primary-color, #1976d2);
    text-decoration: underline;

    &:hover {
      color: var(--primary-color-dark, #1565c0);
      text-decoration: none;
    }
  }
}
</style>
